import { useState, useRef } from 'react';
import { LoaderBalloonScreen, Panel, Button, Select } from '@crazyegginc/hatch';
import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';

import { isSemrush } from '/src/utils/semrush';
import { escapeHTML } from '/src/utils/html';
import { useAuthContext, useMutation, useModal, useNotifications } from '/src/hooks';
import { addCETag, snapshotAddonListDetails } from '/src/features/account/utils';
import { pricingGridQuery } from '../queries';
import { createCustomCheckoutSessionMutation, updateSubscriptionItemsMutation } from '/src/features/billing/mutations';
import { PricingCard } from '/src/features/billing/components/PricingCard';
import { PricingIntervalToggle } from '/src/features/billing/components/PricingIntervalToggle';

import { SnapshotAddonModal } from '/src/features/account/components/modals/SnapshotAddonModal';
import { AppLink } from '/src/features/_global/components/AppLink';
import { SupportLinks } from '/src/support';
import { SupportLink } from '/src/features/_global/components/SupportLink';
import { WebP } from '/src/components/WebP';

import { ReactComponent as BalloonIcon } from '@crazyegginc/hatch/dist/images/logo-ce-balloon.svg';
import { ReactComponent as CrossIcon } from '@crazyegginc/hatch/dist/images/icon-cross.svg';
import HelpPNG from '@crazyegginc/hatch/dist/images/illustration-help.png';
import HelpWebP from '@crazyegginc/hatch/dist/images/illustration-help.webp';

import { PLAN_FREE_FEATURES, PLAN_ORDERS, PLAN_TYPES, SUBSCRIPTION_STATES } from '/src/features/_global/constants';

import {
  SNAPSHOT_ADDON_50,
  SNAPSHOT_ADDON_100,
  SNAPSHOT_ADDON_200,
  SNAPSHOT_ADDON_UNLIMITED,
} from '/src/features/account/constants';

const snapshotAddonList = [SNAPSHOT_ADDON_50, SNAPSHOT_ADDON_100, SNAPSHOT_ADDON_200, SNAPSHOT_ADDON_UNLIMITED];

function transformFeatureText(text) {
  if (!text) return '';
  return escapeHTML(text).replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
}

function splitFeatures(features = []) {
  const dividerIndex = features.findIndex((f) => {
    const text = typeof f === 'string' ? f : f.name;
    return text?.includes('---');
  });

  const getText = (f) => (typeof f === 'string' ? f : f.name);

  if (dividerIndex === -1) {
    return {
      top_features: features.map((f) => transformFeatureText(getText(f))).filter(Boolean),
      bottom_features: [],
    };
  }

  return {
    top_features: features
      .slice(0, dividerIndex)
      .map((f) => transformFeatureText(getText(f)))
      .filter(Boolean),
    bottom_features: features
      .slice(dividerIndex + 1)
      .map((f) => transformFeatureText(getText(f)))
      .filter(Boolean),
  };
}

function getOrderedPlans(plans, isFree, isSemrush) {
  const order = isSemrush ? PLAN_ORDERS.semrush : isFree ? PLAN_ORDERS.free : PLAN_ORDERS.paid;

  return order
    .map((name) => {
      const plan = plans.find((p) => p.name === name);

      if (!plan) return null;

      const features = name === 'free' ? PLAN_FREE_FEATURES : plan.features;
      const { top_features, bottom_features } = splitFeatures(features);

      return { ...plan, features, top_features, bottom_features };
    })
    .filter(Boolean);
}

function PlanAddons({ snapshotAddon, setSnapshotAddon, currentUser }) {
  const modal = useModal();

  return (
    <section className="flex justify-between w-full">
      <div className="flex flex-col">
        <h3 className="text-header-5 mb-3">Add Heatmaps to your plan:</h3>
        <SnapshotAddOnOptions snapshotAddon={snapshotAddon} setSnapshotAddon={setSnapshotAddon} />

        <Button
          disabled={!snapshotAddon.type}
          onClick={() => {
            addCETag('snapshots_addon');
            modal.show(<SnapshotAddonModal planDetail={snapshotAddon} email={currentUser.email} />);
          }}
          className="mt-6 self-start"
        >
          Add Heatmaps
        </Button>
      </div>
      <div className="w-[140px]">
        <div className="mb-0.5 flex w-full justify-end">
          <span className=" self-start text-md">$</span>
          <span className="self-start text-[32px] leading-none">{snapshotAddon.costPerMonth}</span>
          <span className="ml-0.5 self-end text-base"> / month</span>
        </div>
        <p className="w-full pr-5 text-right text-xs text-lynch-500">
          ${(snapshotAddon.costPerMonth * 12).toLocaleString()} / year
        </p>
      </div>
    </section>
  );
}

function SnapshotAddOnOptions({ snapshotAddon, setSnapshotAddon }) {
  return (
    <Select
      name="planAddOn"
      options={snapshotAddonList.map((addon) => {
        const addonDetail = snapshotAddonListDetails(addon);
        const isAddonUnlimited = addon === SNAPSHOT_ADDON_UNLIMITED;

        return {
          value: addon,
          label: `${isAddonUnlimited ? 'Unlimited' : addonDetail.snapshots} ${
            !isAddonUnlimited ? 'extra' : ''
          } Heatmaps`,
          disabled: snapshotAddon.type === addon,
        };
      })}
      placeholder={`${snapshotAddon.type === SNAPSHOT_ADDON_UNLIMITED ? 'Unlimited' : snapshotAddon.snapshots} ${
        snapshotAddon.type !== SNAPSHOT_ADDON_UNLIMITED ? 'extra' : ''
      } Heatmaps`}
      onChange={async (value) => {
        const addonDetail = snapshotAddonListDetails(value);

        await setSnapshotAddon({
          costPerMonth: addonDetail.costPerMonth,
          snapshots: addonDetail.snapshots,
          type: addonDetail.type ? value : false,
        });
      }}
      style={{
        background: '#e5f2fc',
        borderColor: '#e5f2fc',
        cursor: 'pointer',
        color: '#0086e6',
        fontWeight: 'bold',
      }}
    />
  );
}

export function SubscribePage() {
  const notifications = useNotifications();
  const { currentUser, subscription, currentAccount } = useAuthContext();
  const { data, isLoading, isError, isFetching, refetch } = useQuery(pricingGridQuery());
  const [selectedInterval, setSelectedInterval] = useState(() => {
    const planType = subscription?.plan?.type;
    const isFreeOrTrial = planType === PLAN_TYPES.FREE || planType === PLAN_TYPES.TRIAL;
    const period = subscription?.plan?.period?.toLowerCase?.() ?? null;
    return !isFreeOrTrial && period === 'monthly' ? 'monthly' : 'yearly';
  });
  const [snapshotAddon, setSnapshotAddon] = useState(snapshotAddonListDetails(SNAPSHOT_ADDON_50));
  const checkoutMutation = useMutation(createCustomCheckoutSessionMutation);
  const updateMutation = useMutation(updateSubscriptionItemsMutation);
  const isRedirecting = useRef(false);
  const isSubmitting = checkoutMutation.isPending || updateMutation.isPending || isRedirecting.current;

  const pricingGrid = data?.pricingGrid ?? {};
  const isAddonHeatmap = pricingGrid?.showAddOnHeatmaps;
  const planList = pricingGrid?.plans;
  const currentPlan = pricingGrid?.currentPlan?.toLowerCase();
  const isFree = currentPlan === 'free';
  const pricingIntervals = pricingGrid?.availableIntervals ?? [];
  const hasMonthlyAndYearlyIntervals = pricingIntervals.includes('monthly') && pricingIntervals.includes('yearly');
  const showIntervalToggle = hasMonthlyAndYearlyIntervals && !isSemrush;
  const orderedPlans = planList && currentPlan ? getOrderedPlans(planList, isFree, isSemrush) : null;
  const isStripeUser = Boolean(currentAccount?.useStripe);
  const isSemRushTemporaryPlan = isSemrush && subscription?.state === SUBSCRIPTION_STATES.TEMPORARY;
  const isMissingPricingData = !planList || !currentPlan;

  function handleStripeSubscribe({ id }) {
    const mutationArgs = { planPriceId: id };
    const onError = () => {
      isRedirecting.current = false;
      notifications.error({ content: `Failed to update subscription. Please try again.`, timeout: 3000 });
    };
    const redirectTo = (url) => {
      if (url) {
        isRedirecting.current = true;
        window.location.href = url;
      }
    };

    if (isStripeUser) {
      updateMutation.mutate(mutationArgs, {
        onSuccess: (data) => redirectTo(data?.updateSubscriptionItems?.url),
        onError,
      });
    } else {
      checkoutMutation.mutate(mutationArgs, {
        onSuccess: (data) => redirectTo(data?.createCustomCheckoutSession?.checkoutUrl),
        onError,
      });
    }
  }

  if (isLoading || (!isError && isMissingPricingData && isFetching)) {
    return <LoaderBalloonScreen text="Loading subscription options..." />;
  }

  if (isError || isMissingPricingData) {
    return (
      <div className="flex flex-col bg-white-lilac-500 px-10 pt-5 pb-10 w-full h-full">
        <div className="mb-5 flex justify-between items-center">
          <BalloonIcon className="w-[21px] fill-current text-lima-500" aria-label="Crazy Egg logo" />
          {!isSemRushTemporaryPlan ? (
            <AppLink to="/account/plan" className="h-4 w-4 flex items-center">
              <CrossIcon className="h-4 w-4 fill-current text-cadet-blue-500" aria-label="back to plan page" />
            </AppLink>
          ) : null}
        </div>

        <Panel className="m-auto max-w-[520px] text-center font-sans text-black-pearl-500">
          <h1 className="mb-3 text-2xl font-semibold">Unable to load subscription options</h1>
          <p className="mb-6 text-sm text-lynch-500">
            We couldn&apos;t fetch the latest plan details. Please try again, or contact support if the problem
            continues.
          </p>
          <div className="flex justify-center gap-3">
            <Button onClick={() => refetch()} disabled={isFetching}>
              {isFetching ? 'Retrying...' : 'Try again'}
            </Button>
            <Button component={SupportLink} href={SupportLinks.general.newSupportRequest} variant="secondary">
              Contact Support
            </Button>
          </div>
        </Panel>

        <div className="font-sans text-center mt-auto text-sm text-lynch-500 pt-5">
          &copy; Crazy Egg, Inc {new Date().getFullYear()}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-white-lilac-500 px-10 pt-5 pb-10 w-full h-full">
      <div className="mb-5 flex justify-between items-center">
        <BalloonIcon className="w-[21px] fill-current text-lima-500" aria-label="Crazy Egg logo" />
        {!isSemRushTemporaryPlan ? (
          <AppLink to="/account/plan" className="h-4 w-4 flex items-center">
            <CrossIcon className="h-4 w-4 fill-current text-cadet-blue-500" aria-label="back to plan page" />
          </AppLink>
        ) : null}
      </div>

      <div
        className={classNames('flex justify-center flex-col text-center', {
          'mb-10': !showIntervalToggle,
          'mb-6': showIntervalToggle,
        })}
      >
        <h1 className="font-sans font-semibold text-4xl">Change Plan</h1>
        {showIntervalToggle ? (
          <PricingIntervalToggle selectedInterval={selectedInterval} setSelectedInterval={setSelectedInterval} />
        ) : null}
      </div>

      <div className="max-w-[1440px] mx-auto">
        {orderedPlans ? (
          <ul className={classNames('grid gap-5 mb-5', { 'grid-cols-3': isSemrush, 'grid-cols-4': !isSemrush })}>
            {orderedPlans.map((plan) => (
              <PricingCard
                key={plan.name}
                plan={plan}
                selectedInterval={selectedInterval}
                currentPlan={currentPlan}
                subscription={subscription}
                isSubmitting={isSubmitting}
                onSubscribe={handleStripeSubscribe}
                isSemrush={isSemrush}
                currentUser={currentUser}
              />
            ))}
          </ul>
        ) : null}

        <div className="flex gap-5 text-black-pearl-500 font-sans">
          <Panel className="self-start">
            <div className="flex flex-row justify-between w-full items-center">
              <div className="flex items-center">
                <WebP webp={HelpWebP} fallback={HelpPNG} width="40px" alt="Help with account" />
                <div className="ml-5">
                  <h3 className="font-semibold mb-1 text-xl">Contact Us</h3>
                  <p className="text-sm">Get in touch with questions about your account.</p>
                </div>
              </div>
              <Button component={SupportLink} href={SupportLinks.general.newSupportRequest} variant="secondary">
                Contact Support
              </Button>
            </div>
          </Panel>

          {isAddonHeatmap ? (
            <Panel>
              <PlanAddons snapshotAddon={snapshotAddon} setSnapshotAddon={setSnapshotAddon} currentUser={currentUser} />
            </Panel>
          ) : null}
        </div>
      </div>

      <div className="font-sans text-center mt-auto text-sm text-lynch-500 pt-5">
        &copy; Crazy Egg, Inc {new Date().getFullYear()}
      </div>
    </div>
  );
}
